import { isWeChatBrowser } from "../utils/browser";

function isProbablyMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

function dispatchVenmoFallback({ webUrl, sourceModalId }) {
  if (typeof window === "undefined") return false;
  const cleanWebUrl = String(webUrl || "").trim();
  const cleanSourceModalId = String(sourceModalId || "").trim();
  if (!cleanWebUrl) return false;
  try {
    const currentUrl = (() => {
      try {
        return typeof window !== "undefined" ? String(window.location?.href || "") : "";
      } catch {
        return "";
      }
    })();
    window.dispatchEvent(
      new CustomEvent("as:venmo-fallback", {
        detail: {
          pageUrl: currentUrl || undefined,
          venmoWebUrl: cleanWebUrl,
          sourceModalId: cleanSourceModalId || undefined,
        },
      })
    );
    return true;
  } catch {
    // If CustomEvent isn't available, fall back to opening the URL.
    return openInNewTabOrSameTab(cleanWebUrl);
  }
}

function openInNewTabOrSameTab(url) {
  const safeUrl = String(url || "").trim();
  if (!safeUrl) return false;
  try {
    const win = window.open(safeUrl, "_blank", "noopener,noreferrer");
    if (win) return true;
  } catch {
    // ignore
  }
  try {
    window.location.assign(safeUrl);
    return true;
  } catch {
    return false;
  }
}

function openDeepLinkWithFallback({ appUrl, webUrl, timeoutMs = 2500, sourceModalId }) {
  if (typeof window === "undefined") return false;
  const cleanAppUrl = String(appUrl || "").trim();
  const cleanWebUrl = String(webUrl || "").trim();
  if (!cleanAppUrl || !cleanWebUrl) return false;
  const cleanSourceModalId = String(sourceModalId || "").trim();

  // WeChat in-app browser often blocks deep links and can behave inconsistently.
  // Skip attempting the deep link and show the guided fallback modal immediately.
  if (isWeChatBrowser()) {
    return dispatchVenmoFallback({ webUrl: cleanWebUrl, sourceModalId: cleanSourceModalId });
  }

  // Desktop/non-mobile: just open the web URL.
  if (!isProbablyMobile()) return openInNewTabOrSameTab(cleanWebUrl);

  // Mobile: try deep link; fall back only if the page never backgrounds/loses focus.
  let didBackgroundOrBlur = false;
  const startedAt = Date.now();

  const markSuccess = () => {
    didBackgroundOrBlur = true;
  };

  const onVisibilityChange = () => {
    if (document.hidden) markSuccess();
  };

  const cleanup = () => {
    try {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    } catch {
      // ignore
    }
    try {
      window.removeEventListener("pagehide", markSuccess);
    } catch {
      // ignore
    }
    try {
      window.removeEventListener("blur", markSuccess);
    } catch {
      // ignore
    }
  };

  try {
    document.addEventListener("visibilitychange", onVisibilityChange, { passive: true });
  } catch {
    // ignore
  }
  try {
    window.addEventListener("pagehide", markSuccess, { passive: true });
  } catch {
    // ignore
  }
  try {
    window.addEventListener("blur", markSuccess, { passive: true });
  } catch {
    // ignore
  }

  // Kick off the deep link.
  window.location.assign(cleanAppUrl);

  window.setTimeout(() => {
    const elapsed = Date.now() - startedAt;
    cleanup();

    // If we backgrounded/blurred at any point, assume the app opened.
    if (didBackgroundOrBlur) return;

    // If the timer fired "late" (JS paused while switching apps), assume the app opened.
    if (elapsed > timeoutMs + 600) return;

    // Instead of silently opening the web fallback, show a guided modal.
    dispatchVenmoFallback({ webUrl: cleanWebUrl, sourceModalId: cleanSourceModalId });
  }, timeoutMs);

  return true;
}

export function buildVenmoPayUrls({ username, amountCents, note }) {
  const cleanUsername = String(username || "").trim().replace(/^@/, "");
  const amt = Number(amountCents || 0);
  const amount = (amt / 100).toFixed(2);
  const cleanNote = String(note || "").trim();

  // Venmo app deep link
  // Example: venmo://paycharge?txn=pay&recipients=Tom-Cat-11&amount=12.34&note=Trip%20to%20SF
  const appUrl =
    "venmo://paycharge?" +
    `txn=pay&recipients=${encodeURIComponent(cleanUsername)}` +
    `&amount=${encodeURIComponent(amount)}` +
    (cleanNote ? `&note=${encodeURIComponent(cleanNote)}` : "");

  // Web fallback (profile page; user can tap Pay in Venmo web/app)
  const webUrl = `https://venmo.com/u/${encodeURIComponent(cleanUsername)}`;

  return { appUrl, webUrl };
}

export function openVenmoPay({ username, amountCents, note, sourceModalId } = {}) {
  const cleanUsername = String(username || "").trim().replace(/^@/, "");
  if (!cleanUsername) return false;

  const { appUrl, webUrl } = buildVenmoPayUrls({ username: cleanUsername, amountCents, note });
  return openDeepLinkWithFallback({ appUrl, webUrl, timeoutMs: 1500, sourceModalId });
}

export function openVenmoProfile({ username, sourceModalId } = {}) {
  const cleanUsername = String(username || "").trim().replace(/^@/, "");
  const appUrl = cleanUsername
    ? "venmo://paycharge?" +
      `txn=pay&recipients=${encodeURIComponent(cleanUsername)}`
    : "venmo://";
  const webUrl = cleanUsername
    ? `https://account.venmo.com/u/${encodeURIComponent(cleanUsername)}`
    : "https://account.venmo.com/u/profile";

  return openDeepLinkWithFallback({ appUrl, webUrl, timeoutMs: 2500, sourceModalId });
}

